import { useEffect, useState, useCallback } from "react";
import api from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { HandHeart, Lock, Globe, Heart } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  novo: { label: "Novo", cls: "bg-[hsl(var(--sky))]/15 text-[hsl(var(--sky))]" },
  orando: { label: "Em oração", cls: "bg-accent/15 text-accent" },
  respondido: { label: "Respondido", cls: "bg-[hsl(var(--emerald))]/15 text-[hsl(var(--emerald))]" },
};
const NEXT = { novo: "orando", orando: "respondido", respondido: "novo" };

export default function Prayers() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("todos");

  const load = useCallback(() => {
    const params = filter === "todos" ? {} : { status: filter };
    api.get("/prayers", { params }).then((r) => setItems(r.data)).catch(() => {});
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (p, status) => {
    try {
      await api.patch(`/prayers/${p.id}/status`, { status });
      toast.success("Status atualizado");
      load();
    } catch {
      toast.error("Erro ao atualizar");
    }
  };

  const counts = {
    novo: items.filter((i) => i.status === "novo").length,
    orando: items.filter((i) => i.status === "orando").length,
    respondido: items.filter((i) => i.status === "respondido").length,
  };

  return (
    <div className="space-y-8 animate-fade-up" data-testid="prayers-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Pedidos de Oração</h1>
          <p className="text-muted-foreground mt-1">Acompanhe e interceda pelos pedidos dos participantes (inclui confidenciais).</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48" data-testid="prayer-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            <SelectItem value="novo">Novos</SelectItem>
            <SelectItem value="orando">Em oração</SelectItem>
            <SelectItem value="respondido">Respondidos</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {Object.entries(STATUS).map(([k, v]) => (
          <Card key={k} className="p-5">
            <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{v.label}</div>
            <div className="font-display text-3xl font-bold mt-2">{counts[k]}</div>
          </Card>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => {
          const sm = STATUS[p.status] || STATUS.novo;
          return (
            <Card key={p.id} className="p-5 flex flex-col" data-testid="prayer-manage-card">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold">{p.name}</span>
                {p.visibility === "confidencial"
                  ? <Badge variant="secondary" className="text-xs gap-1"><Lock className="h-3 w-3" /> Confidencial</Badge>
                  : <Badge variant="secondary" className="text-xs gap-1"><Globe className="h-3 w-3" /> Público</Badge>}
              </div>
              {p.group_name && <span className="text-xs text-muted-foreground mt-1">{p.group_name}</span>}
              <p className="text-sm text-muted-foreground mt-3 flex-1 leading-relaxed">{p.request}</p>
              <div className="mt-4 flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground flex items-center gap-1"><Heart className="h-3.5 w-3.5 text-accent" /> {p.prayer_count}</span>
                <div className="flex items-center gap-2">
                  <Badge className={`${sm.cls} border-none`}>{sm.label}</Badge>
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setStatus(p, NEXT[p.status])} data-testid={`prayer-advance-${p.id}`}>
                    <HandHeart className="h-3.5 w-3.5" /> Avançar
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
        {items.length === 0 && <p className="text-muted-foreground col-span-full">Nenhum pedido nesta categoria.</p>}
      </div>
    </div>
  );
}
